/* A plan, drawn as a plan.
 *
 * Frappe decides a DocType reads as a Gantt in a `*_calendar.js` beside it:
 * a `field_map` naming the start and end, and `gantt: true`. Those files are
 * harvested into the bundle, so opening one costs no request to find out.
 *
 * Drawn as plain elements rather than with a charting library: a Gantt is
 * bars on a grid, and a dependency is a line between two of them. The whole
 * thing is a few hundred lines and it themes, prints and keyboards like the
 * rest of the app, which a canvas would not.
 *
 * Moving work (owner, 2026-10-09: "drag and drop … and timeline gets shifted"):
 * when the screen passes `onMove`, a bar can be dragged to shift it, and either
 * end dragged to change its start or its deadline, snapped to whole days; with
 * the keyboard, Alt+arrows shift a focused bar a day and Alt+Shift+arrows move
 * its end. A click that does not move still opens it. The bar shows its new
 * place at once and goes back if the move is refused.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent as ReactPointerEvent } from 'react'
import { parseFrappeDate } from './format'
import type { Doc } from './types'
import declared from './views.generated.json'

export type GanttConfig = {
  start: string
  end: string
  title?: string
  id?: string
  progress?: string
  color?: string
  gantt?: boolean
}

/** What this DocType's own `*_calendar.js` says, if it says anything.
 *
 *  Only the ones that set `gantt: true` are offered. Plenty of DocTypes have
 *  a start and an end -- a Gantt of every ToDo is not a plan anybody asked
 *  for, and ERPNext has already made that judgement for us. */
export function ganttConfig(doctype: string): GanttConfig | null {
  const found = (declared as Record<string, GanttConfig>)[doctype]
  return found?.gantt ? found : null
}

/** The fields a Gantt needs on each row, beyond what the table shows. */
export function ganttFields(config: GanttConfig): string[] {
  return [config.start, config.end, config.title, config.progress, config.color,
          // Frappe's own Gantt reads dependencies from this one comma-separated
          // field rather than from a child table, so it costs nothing to ask.
          'depends_on_tasks']
    .filter((f): f is string => Boolean(f))
}

export type Scale = 'day' | 'week' | 'month'

const DAY = 864e5
const WIDTH: Record<Scale, number> = { day: 34, week: 14, month: 5 }
/** How far a pointer travels before a press becomes a drag, not a click. */
const DRAG_PX = 4

/** Midnight, so a bar that starts at 09:00 still begins on its own day. */
const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/** A day as YYYY-MM-DD in the browser's own calendar (not UTC: a bar dropped
 *  on the 9th must not be saved as the 8th). */
export function isoDay(t: number): string {
  const d = new Date(t)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Days between two midnights, counted as calendar days (DST-safe). */
const daysBetween = (a: number, b: number) => Math.round((b - a) / DAY)
const addDays = (t: number, n: number) => {
  const d = new Date(t)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n).getTime()
}

type Bar = {
  name: string
  label: string
  from: number
  to: number
  progress: number
  depends: string[]
}

type Drag = { name: string; mode: 'move' | 'start' | 'end'; x0: number; from: number; to: number; moved: boolean }

/** The header's labels at one scale: months along the top, days or weeks under
 *  them, so a label never runs into its neighbour (owner, 2026-10-09: "in the
 *  plan if I choose days the dates are overlapping"). */
export function headerTicks(spanFrom: number, days: number, scale: Scale, px: number) {
  const months: { left: number; label: string }[] = []
  const ticks: { left: number; label: string; sub: string; strong: boolean }[] = []
  for (let i = 0; i <= days; i++) {
    const day = new Date(addDays(spanFrom, i))
    const first = day.getDate() === 1
    if (i === 0 || first) {
      months.push({ left: i * px, label: day.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) })
    }
    if (scale === 'day') {
      ticks.push({ left: i * px, label: String(day.getDate()).padStart(2, '0'),
                   sub: day.toLocaleDateString('en-IN', { weekday: 'short' }).slice(0, 2), strong: first })
    } else if (scale === 'week' && day.getDay() === 1) {
      ticks.push({ left: i * px, label: String(day.getDate()).padStart(2, '0'), sub: '', strong: first })
    } else if (scale === 'month' && first) {
      ticks.push({ left: i * px, label: '', sub: '', strong: true })
    }
  }
  // The opening month's label gives way when the next month starts too close to fit both.
  const MONTH_PX = 64
  const roomy = months.filter((m, i) => {
    const next = months[i + 1]
    return m.left !== 0 || !next || next.left - m.left >= MONTH_PX
  })
  return { months: roomy, ticks }
}

export function GanttView({ rows, config, onOpen, title, onMove, canMove }: {
  rows: Doc[]
  config: GanttConfig
  onOpen?: (name: string) => void
  title?: string
  /** Save a bar's new dates (YYYY-MM-DD); reject to put it back. Without it the plan is read-only. */
  onMove?: (name: string, from: string, to: string) => Promise<unknown>
  /** Which bars this person may move (default: all, when onMove is given). */
  canMove?: (name: string) => boolean
}) {
  const [scale, setScale] = useState<Scale>('week')
  const scroller = useRef<HTMLDivElement>(null)
  /** Where bars are while being moved or saved, by name. */
  const [moved, setMoved] = useState<Record<string, { from: number; to: number }>>({})
  const [drag, setDrag] = useState<Drag | null>(null)
  const [said, setSaid] = useState('')
  const suppressClick = useRef(false)

  const bars = useMemo<Bar[]>(() => {
    const out: Bar[] = []
    for (const row of rows) {
      const from = parseFrappeDate(row[config.start])
      const to = parseFrappeDate(row[config.end]) ?? from
      // A row with no dates is not a bar. It is left out rather than drawn at
      // the epoch, which is what stretches a chart to 1970 and hides the work.
      if (!from || !to) continue
      const raw = config.progress ? Number(row[config.progress]) : 0
      out.push({
        name: String(row.name),
        label: String((config.title && row[config.title]) || row.name),
        from: midnight(from),
        to: Math.max(midnight(to), midnight(from)),
        progress: Number.isFinite(raw) ? Math.min(Math.max(raw, 0), 100) : 0,
        depends: String(row['depends_on_tasks'] ?? '')
          .split(',').map((s) => s.trim()).filter(Boolean),
      })
    }
    return out.sort((a, b) => a.from - b.from || a.label.localeCompare(b.label))
  }, [rows, config])

  // Saved: the rows arrive with the new dates, so the local copy goes.
  useEffect(() => { setMoved({}) }, [rows])

  const placed = useMemo(() => bars.map((b) => ({ ...b, ...(moved[b.name] ?? {}) })), [bars, moved])

  const span = useMemo(() => {
    if (!bars.length) return null
    const first = Math.min(...bars.map((b) => b.from))
    const last = Math.max(...bars.map((b) => b.to))
    // Air either side, so a bar never touches the edge and can be dragged past it.
    const from = addDays(Math.min(first, midnight(new Date())), -7)
    const to = Math.max(addDays(last, 14), addDays(from, 28))
    return { from, to, days: daysBetween(from, to) + 1 }
  }, [bars])

  const px = WIDTH[scale]

  // Open on today rather than on the earliest bar, which may be months back.
  useEffect(() => {
    const el = scroller.current
    if (!el || !span) return
    el.scrollLeft = Math.max(0, daysBetween(span.from, midnight(new Date())) * px - el.clientWidth / 3)
  // Only when the scale or the range changes, never under a person's own scrolling.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, span?.from, span?.to])

  if (!bars.length || !span) {
    return (
      <div className="card card-pad muted small">
        Nothing here has both a start and an end date, so there is no plan to draw yet.
      </div>
    )
  }

  const x = (t: number) => daysBetween(span.from, t) * px
  const today = midnight(new Date())
  const rowHeight = 32
  const at = new Map(placed.map((b, i) => [b.name, i]))
  const { months, ticks } = headerTicks(span.from, span.days, scale, px)
  const movable = (name: string) => Boolean(onMove) && (canMove ? canMove(name) : true)
  const fmt = (t: number) => new Date(t).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
  const range = (from: number, to: number) => `${fmt(from)}${to !== from ? ` – ${fmt(to)}` : ''}`

  async function commit(name: string, from: number, to: number, before: { from: number; to: number }) {
    if (!onMove || (from === before.from && to === before.to)) return
    setMoved((m) => ({ ...m, [name]: { from, to } }))
    setSaid(`Moving to ${range(from, to)}…`)
    try {
      await onMove(name, isoDay(from), isoDay(to))
      setSaid(`Moved to ${range(from, to)}.`)
    } catch {
      // Refused: back where it was. The screen says why.
      setMoved((m) => { const { [name]: _gone, ...rest } = m; return rest })
      setSaid('Not moved.')
    }
  }

  function onPointerDown(e: ReactPointerEvent<HTMLElement>, b: Bar, mode: Drag['mode']) {
    if (!movable(b.name) || e.button !== 0) return
    e.stopPropagation()
    setDrag({ name: b.name, mode, x0: e.clientX, from: b.from, to: b.to, moved: false })
  }

  function onPointerMove(e: ReactPointerEvent<HTMLElement>) {
    if (!drag) return
    const dx = e.clientX - drag.x0
    if (!drag.moved && Math.abs(dx) < DRAG_PX) return
    const days = Math.round(dx / px)
    let from = drag.from
    let to = drag.to
    if (drag.mode === 'move') { from = addDays(drag.from, days); to = addDays(drag.to, days) }
    if (drag.mode === 'start') from = Math.min(addDays(drag.from, days), drag.to)
    if (drag.mode === 'end') to = Math.max(addDays(drag.to, days), drag.from)
    if (!drag.moved) setDrag({ ...drag, moved: true })
    setMoved((m) => ({ ...m, [drag.name]: { from, to } }))
    setSaid(range(from, to))
  }

  function onPointerUp() {
    if (!drag) return
    const now = moved[drag.name]
    if (drag.moved && now) {
      // The click a drag ends with is not an "open"; cleared after this event either way.
      suppressClick.current = true
      setTimeout(() => { suppressClick.current = false }, 0)
      void commit(drag.name, now.from, now.to, { from: drag.from, to: drag.to })
    }
    setDrag(null)
  }

  function onKey(e: KeyboardEvent<HTMLButtonElement>, b: Bar) {
    if (!movable(b.name) || !e.altKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return
    e.preventDefault()
    const step = e.key === 'ArrowRight' ? 1 : -1
    const now = moved[b.name] ?? { from: b.from, to: b.to }
    const next = e.shiftKey
      ? { from: now.from, to: Math.max(addDays(now.to, step), now.from) }
      : { from: addDays(now.from, step), to: addDays(now.to, step) }
    void commit(b.name, next.from, next.to, { from: b.from, to: b.to })
  }

  return (
    <div className="gantt">
      <div className="row-between" style={{ marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div className="small muted">
          {bars.length} item{bars.length === 1 ? '' : 's'}{title ? ` · ${title}` : ''}
          {onMove ? ' · drag a bar to move it, or an end to change its dates (Alt+arrows on the keyboard)' : ''}
        </div>
        <div className="row" role="group" aria-label="Timeline scale" style={{ gap: 4 }}>
          <button type="button" className="btn btn-sm btn-quiet"
                  onClick={() => { const el = scroller.current; if (el) el.scrollLeft = Math.max(0, x(today) - el.clientWidth / 3) }}>
            Today
          </button>
          {(['day', 'week', 'month'] as Scale[]).map((s) => (
            <button key={s} type="button" className="btn btn-sm"
                    data-on={scale === s || undefined} aria-pressed={scale === s}
                    onClick={() => setScale(s)}>
              {s === 'day' ? 'Days' : s === 'week' ? 'Weeks' : 'Months'}
            </button>
          ))}
        </div>
      </div>
      <div className="sr-only" role="status" aria-live="polite">{said}</div>

      <div className="gantt-frame">
        <div className="gantt-names" aria-hidden="true">
          <div className="gantt-head gantt-head-2" />
          {placed.map((b) => (
            <div key={b.name} className="gantt-name" style={{ height: rowHeight }} title={b.label}>
              {b.label}
            </div>
          ))}
        </div>

        <div className="gantt-scroll" ref={scroller}>
          <div style={{ width: span.days * px, position: 'relative' }}>
            <div className="gantt-head gantt-head-2">
              {months.map((m) => (
                <span key={`m${m.left}`} className="gantt-month" style={{ left: m.left }}>{m.label}</span>
              ))}
              {ticks.map((t) => (
                <span key={t.left} className="gantt-tick gantt-tick-2" data-strong={t.strong || undefined}
                      data-today={x(today) === t.left || undefined}
                      style={{ left: t.left }}>
                  {t.label}{t.sub ? <small>{t.sub}</small> : null}
                </span>
              ))}
            </div>

            <div style={{ position: 'relative', height: placed.length * rowHeight }}
                 onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
                 onPointerCancel={onPointerUp}>
              {ticks.map((t) => (
                <span key={`g${t.left}`} className="gantt-grid" data-strong={t.strong || undefined}
                      style={{ left: t.left }} />
              ))}
              {today >= span.from && today <= span.to && (
                <span className="gantt-today" style={{ left: x(today) }} aria-hidden="true" />
              )}

              {/* Dependencies, drawn behind the bars so a bar is never hidden. */}
              <svg className="gantt-links" width={span.days * px} height={placed.length * rowHeight}
                   aria-hidden="true">
                {placed.flatMap((b, i) => b.depends.flatMap((dep) => {
                  const j = at.get(dep)
                  if (j === undefined) return []
                  const fromX = x(addDays(placed[j]!.to, 1))
                  const fromY = j * rowHeight + rowHeight / 2
                  const toX = x(b.from)
                  const toY = i * rowHeight + rowHeight / 2
                  const mid = Math.max(fromX + 8, toX - 8)
                  return [
                    <path key={`${b.name}-${dep}`}
                          d={`M ${fromX} ${fromY} H ${mid} V ${toY} H ${toX}`}
                          className="gantt-link" />,
                  ]
                }))}
              </svg>

              {placed.map((b, i) => {
                const left = x(b.from)
                const width = Math.max(x(addDays(b.to, 1)) - left, 6)
                const late = b.to < today && b.progress < 100
                const can = movable(b.name)
                const dates = `${new Date(b.from).toLocaleDateString('en-IN')} to ${new Date(b.to).toLocaleDateString('en-IN')}`
                return (
                  <button
                    key={b.name} type="button" className="gantt-bar" data-late={late || undefined}
                    data-movable={can || undefined} data-dragging={drag?.name === b.name || undefined}
                    style={{ left, width, top: i * rowHeight + 5 }}
                    onPointerDown={(e) => onPointerDown(e, b, 'move')}
                    onKeyDown={(e) => onKey(e, b)}
                    onClick={() => {
                      if (suppressClick.current) { suppressClick.current = false; return }
                      onOpen?.(b.name)
                    }}
                    title={`${b.label} · ${dates}${b.progress ? ` · ${b.progress}% done` : ''}`}
                    aria-label={`${b.label}, ${dates}${b.progress ? `, ${b.progress} percent done` : ''}${late ? ', overdue' : ''}${can ? '. Alt and the arrows move it.' : ''}`}
                  >
                    {b.progress > 0 && (
                      <span className="gantt-fill" style={{ width: `${b.progress}%` }} aria-hidden="true" />
                    )}
                    {can ? <span className="gantt-handle" data-side="start" aria-hidden="true"
                                 onPointerDown={(e) => onPointerDown(e, b, 'start')} /> : null}
                    <span className="gantt-bar-label">{b.label}</span>
                    {can ? <span className="gantt-handle" data-side="end" aria-hidden="true"
                                 onPointerDown={(e) => onPointerDown(e, b, 'end')} /> : null}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
